import React from 'react'
import { useFormContext, ValidationRules } from 'react-hook-form'
import { Input, InputProps } from '@rebass/forms'
import { Box } from 'rebass'
import ConditionalRender from 'utils/conditionalRender'
import { InputStyle } from './styles'
import { useSTheme } from 'styles/theme'
import FInputError from '../FInputError'
import FLabel from '../FLabel'
import { input } from '../constants'
import { useTranslation } from '../../../i18n'
import { ga4_inputChanged } from 'utils/ga4'

const FInputTypeEnum = {
    TEXT: 'text',
    PASSWORD: 'password',
    NUMBER: 'number',
    MONEY: 'money',
    EMAIL: 'email',
} as const

type FInputType = typeof FInputTypeEnum[keyof typeof FInputTypeEnum]

type Props<T = string | number> = InputProps & {
    id: string
    alwaysDatePicker?: boolean
    datePicker?: boolean
    type: FInputType
    label: string
    readOnly?: boolean
    noMargin?: boolean
    fullParent?: boolean
    placeholder?: string
    onKeyPress?: any
    onPaste?: any
    validators?: ValidationRules
    value?: T
    initialValue?: T
    labelColor?: string
}

const FInput = React.forwardRef((props: Props, ref) => {
    const { id, label, placeholder, readOnly, type, initialValue, onKeyPress, onPaste, value, ...otherProps } = props
    const { errors } = useFormContext()
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    let datePickerProps = {}

    if (props.alwaysDatePicker) {
        datePickerProps = {
            '::-webkit-calendar-picker-indicator': {
                background: 'transparent',
                bottom: '0',
                color: 'transparent',
                cursor: 'pointer',
                height: 'auto',
                left: '0',
                position: 'absolute',
                right: '0',
                top: '0',
                width: 'auto',
            },
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: props.noMargin ? '0px' : input.desktop.marginTop,
                width: props.fullParent ? '100%' : 'auto',
            }}
        >
            <ConditionalRender if={label}>
                <FLabel color={props.labelColor || theme.colors.gray400} sx={{ fontWeight: '700' }}>
                    {label}
                </FLabel>
            </ConditionalRender>
            <Input
                sx={InputStyle(theme, readOnly, datePickerProps, props?.disabled)}
                id={id}
                name={id}
                type={type}
                readOnly={readOnly}
                placeholder={placeholder}
                value={value}
                defaultValue={initialValue}
                onKeyPress={onKeyPress}
                onPaste={onPaste}
                onChange={() => ga4_inputChanged(id, !value)}
                ref={ref}
                {...(props.datePicker && otherProps)}
                style={{
                    padding: input.desktop.padding,
                    cursor: props.datePicker ? 'pointer' : 'initial',
                }}
            />
            <ConditionalRender if={errors[id]}>
                <FInputError id={id + '-error'} forElementId={id}>
                    {t(errors[id]?.message)}
                </FInputError>
            </ConditionalRender>
        </Box>
    )
})

FInput.displayName = 'FInput'

export default FInput
